<script setup lang="ts">
import type { GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type { BadgeProps } from '#layers/ui/public';
import { computed } from 'vue';
import { useDataModelDisplayNames } from '#layers/data-model/composables/use-data-model-display-names';
import { useWorkspaceSchemaStore } from '#layers/data-model/stores/workspace-schema';
import { RoundedVariant } from '#layers/ui/public';

const { path, variant = 'soft', size, color, ui, unresolved } = defineProps<{
	path: readonly GlobalFieldId[];
	variant?: BadgeProps['variant'];
	color?: BadgeProps['color'];
	size?: BadgeProps['size'];
	ui?: BadgeProps['ui'];
	unresolved?: boolean;
}>();

const { findPrimitiveFieldById, findSingleRelationFieldById } = useWorkspaceSchemaStore();
const { getPrimitiveFieldDisplayName, getSingleRelationFieldDisplayName } = useDataModelDisplayNames();

const labelParts = computed(() => {
	if (path.length === 0) return [];

	if (path.length === 1) {
		const primitiveField = findPrimitiveFieldById(path[0] as GlobalPrimitiveFieldId);

		if (primitiveField) {
			return [getPrimitiveFieldDisplayName(primitiveField)];
		}

		const singleRelationField = findSingleRelationFieldById(path[0] as GlobalSingleRelationFieldId);

		if (singleRelationField) {
			return [getSingleRelationFieldDisplayName(singleRelationField)];
		}

		return [];
	}

	const labels: string[] = [];

	for (const [index, fieldId] of path.entries()) {
		const isLast = index === path.length - 1;

		const singleRelationField = findSingleRelationFieldById(fieldId as GlobalSingleRelationFieldId);

		if (singleRelationField) {
			labels.push(getSingleRelationFieldDisplayName(singleRelationField));
			continue;
		}

		if (!isLast) break;

		const primitiveField = findPrimitiveFieldById(fieldId as GlobalPrimitiveFieldId);

		if (!primitiveField) break;

		labels.push(getPrimitiveFieldDisplayName(primitiveField));
	}

	return labels;
});

const showUnresolved = computed(() => unresolved || labelParts.value.length !== path.length);
</script>

<template>
	<DBadge :variant :size :color :ui class="leading-none" :rounded="RoundedVariant.Normal">
		<span v-if="labelParts.length === 0">{{ $t('collection_field_select.unknown_field') }}</span>

		<span v-else class="inline-flex items-center gap-1">
			<template v-for="(label, index) in labelParts" :key="`${label}-${index}`">
				<DIcon v-if="index > 0" name="lucide:chevron-right" class="size-3 text-muted" />
				<span :class="index < labelParts.length - 1 ? 'text-muted' : undefined">{{ label }}</span>
			</template>

			<template v-if="showUnresolved">
				<DIcon name="lucide:chevron-right" class="size-3 text-muted" />
				<span>{{ $t('collection_field_select.unknown_field') }}</span>
			</template>
		</span>
	</DBadge>
</template>
